// ===============================================
// File: src/contexts/VoiceTimerContext.jsx
// Description: Voice timer logs (folder-only persistence).
// If no folder selected -> in-memory; writes are no-ops.
// Rehydrates after "mudo:vault-restored" and "mudo:files-restored"
// ===============================================

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useStorage } from "./StorageContext";

const FILE_LOGS   = "timelog/logs.json";
const FILE_ACTIVE = "timelog/active.json";

const VoiceTimerContext = createContext(null);
export const useVoiceTimer = () => useContext(VoiceTimerContext);

export function VoiceTimerProvider({ children }) {
  const { hasFolder, loadJSON, saveJSON } = useStorage();

  const [active, setActive] = useState(null);
  const [logs, setLogs] = useState([]);

  // hydrate from folder
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!hasFolder) { if (alive) { setActive(null); setLogs([]); } return; }
      try {
        const [a, l] = await Promise.all([
          loadJSON(FILE_ACTIVE, null),
          loadJSON(FILE_LOGS, []),
        ]);
        if (!alive) return;
        setActive(a && typeof a === "object" ? a : null);
        setLogs(Array.isArray(l) ? l : []);
      } catch {
        if (!alive) return;
        setActive(null);
        setLogs([]);
      }
    })();
    return () => { alive = false; };
  }, [hasFolder, loadJSON]);

  // rehydrate after Master Receive / Vault restore
  useEffect(() => {
    const rehydrate = async () => {
      if (!hasFolder) return;
      try {
        const [a, l] = await Promise.all([
          loadJSON(FILE_ACTIVE, null),
          loadJSON(FILE_LOGS, []),
        ]);
        setActive(a && typeof a === "object" ? a : null);
        setLogs(Array.isArray(l) ? l : []);
      } catch {}
    };
    window.addEventListener("mudo:vault-restored", rehydrate);
    window.addEventListener("mudo:files-restored", rehydrate);
    return () => {
      window.removeEventListener("mudo:vault-restored", rehydrate);
      window.removeEventListener("mudo:files-restored", rehydrate);
    };
  }, [hasFolder, loadJSON]);

  // persist to folder on change (only if folder linked)
  useEffect(() => {
    if (!hasFolder) return;
    (async () => { try { await saveJSON(FILE_LOGS, logs); } catch {} })();
  }, [logs, hasFolder, saveJSON]);

  useEffect(() => {
    if (!hasFolder) return;
    (async () => { try { await saveJSON(FILE_ACTIVE, active); } catch {} })();
  }, [active, hasFolder, saveJSON]);

  function startTimer({ step, batchId, productType, productId, staffId }) {
    if (!step || !batchId) return { ok: false, error: "Missing step or batchId" };
    if (active) return { ok: false, error: "Another timer is already running" };
    const timer = {
      id: `log_${Date.now()}`,
      step,
      batchId,
      productType: productType || null,
      productId: productId || null,
      staffId: staffId || null,
      startedAt: Date.now(),
    };
    setActive(timer);
    return { ok: true, timer };
    }

  function stopTimer() {
    if (!active) return { ok: false, error: "No timer running" };
    const finishedAt = Date.now();
    // 0.1-minute resolution: (delta/60000) rounded to 0.1
    const minutes = Math.max(
      0.1,
      Math.round((finishedAt - active.startedAt) / 6000) / 10
    );
    const entry = { ...active, finishedAt, minutes };
    setActive(null);
    setLogs((L) => [entry, ...L]);
    return { ok: true, entry };
  }

  function clearLogs() { setLogs([]); }

  const value = useMemo(() => ({ active, logs, startTimer, stopTimer, clearLogs }), [active, logs]);

  return <VoiceTimerContext.Provider value={value}>{children}</VoiceTimerContext.Provider>;
}
